import axios from 'axios';
import { Services } from '@/services/Services';
import { DHLError, getCurrentDate, axiosRequest, handleHttpResponse } from '@general/js/utils';
import { PaymentMethods } from '@general/js/enums';
import PaymentsAbstractServices from './PaymentsAbstractServices';

export default class PaymentsServices extends PaymentsAbstractServices {
  /**
   * Implicitly returns a Promise for maximum payment amount that member can pay.
   *
   * @param {string} memberNumber - member number (policy number) of logged in member.
   * @param {string} currentDate @optional - date string in yyyy-mm-dd format.
   * @returns {number} - maximum amount of payment that member can pay
   */
  static async getMakeAPaymentMaximumAmount(memberNumber, currentDate = getCurrentDate()) {
    try {
      const response = await axiosRequest().get(Services.PAYMENTS.MAKE_A_PAYMENT.GET_MAXIMUM_PAYMENT_AMOUNT(memberNumber), {
        params: {
          currentDate,
        },
      });
      // Below needs to explicitly checked with `undefined` so that it doesn't fail when maximumPaymentAmount = 0 (DE2E-7662)
      if (handleHttpResponse(response) && response.data?.maximumPaymentAmount !== undefined) {
        return response.data.maximumPaymentAmount;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMakeAPaymentMaximumAmount() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for content information on payments.
   *
   * @returns {object} - object of make a payment page reference data
   */
  static async getMakeAPaymentReferenceData() {
    try {
      const response = await axios.get(Services.PAYMENTS.MAKE_A_PAYMENT.GET_REFERENCE_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMakeAPaymentReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for content presented on the PaymentSuccess view
   *
   * @returns {object} object reference data
   */
  static async getPaymentSuccessReferenceData() {
    try {
      const response = await axios.get(Services.PAYMENTS.MAKE_A_PAYMENT.GET_PAYMENT_SUCCESS_REFERENCE_DATA);
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getPaymentSuccessReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for premium account details. Only return for credit card details not bank account.
   *
   * @param {string} memberNumber - member number (policy number) of logged in member.
   * @param {string} currentDate @optional - date string in yyyy-mm-dd format.
   * @returns {object} - object of premium account detail
   */
  static async getCardDetail(memberNumber, currentDate = getCurrentDate()) {
    try {
      const response = await axiosRequest().get(Services.PAYMENTS.MAKE_A_PAYMENT.GET_PREMIUM_ACCOUNT(memberNumber), {
        params: {
          currentDate,
        },
      });

      if (handleHttpResponse(response) && response.data?.premiumPaymentAccount?.accountType?.toUpperCase() === PaymentMethods.CREDIT_CARD) {
        // only return if account type is credit card
        return response.data.premiumPaymentAccount;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getCardDetail() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for content information on payments.
   *
   * @param {string} memberNumber - member number (policy number) of logged in member.
   * @param {string} currentDate @optional - date string in yyyy-mm-dd format. Default to be current date if not given.
   * @returns {object} object reference data
   */
  static async getReferenceData(memberNumber, currentDate = getCurrentDate()) {
    try {
      const response = await axios.get(Services.PAYMENTS.GET_REFERENCE_DATA(memberNumber), {
        params: {
          currentDate,
        },
      });

      if (handleHttpResponse(response)) {
        // return member number string token swapped data
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Returns a promise for fetching data of payments for a given member number.
   *
   * @param {string} memberNumber - member number or policy number of logged in member
   * @returns {Object} object with value 'payments' that contains either an array of payments or null if none are found
   */
  static async getPayments(memberNumber) {
    try {
      const response = await axiosRequest().get(Services.PAYMENTS.GET_PAYMENTS(memberNumber));
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getPayments() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Returns a promise for fetching data of payment document
   *
   * @param {string} requestingPersonCode - Person code of person requesting to view the payment document
   * @param {string} accessingPersonCode - Person code who's payment document is being viewed
   * @param {string} memberNumber - Member number (policy number) of the policy
   * @param {string} documentId - Document Id of the payment document to be fetched
   * @returns {object} - // TODO: Find actual return object/type
   */
  static async getPaymentsDocument(requestingPersonCode, accessingPersonCode, memberNumber, documentId) {
    try {
      const queryParams = {
        params: {
          requestingPersonCode,
          accessingPersonCode,
          memberNumber,
          documentId,
        }, // TODO: put in correct params after EAI has finished
      };

      const response = await axiosRequest().get(Services.PAYMENTS.GET_PAYMENTS_DOCUMENT, queryParams);
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getPaymentsDocument() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Sends an immediate unsolicited payment
   *
   * @param {object} payload contains currentDate 'YYYY-MM-DD', amount, type, token and cardHolderName
   * @returns {object} which contains confirmation of payment with datePaidTo dates
   */
  static async makePayment(memberNumber, payload) {
    try {
      const response = await axios.post(Services.PAYMENTS.MAKE_A_PAYMENT.MAKE_PAYMENT(memberNumber), payload);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`makePayment() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
